import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { getAuthConfig, signOut } from './auth';
import { usePlatform, type PlatformState } from './platform';

/* ---------------------------------------------------------------------------
   The page frame every Cyntox site shares: sidebar with brand, menu, who is
   signed in, version and help; an off-canvas drawer below 1024px; the screen
   itself on the right.

   Navigation is filtered by the signed-in user's capabilities, which come from
   the server-side Role x Resource x Permission matrix. A screen can also be
   present-but-read-only for a role, which is why visibility keys off the
   "view" permission and each screen's own controls key off "edit".
   --------------------------------------------------------------------------- */

export interface NavEntry {
  to: string;
  label: string;
  resource: string;
  /** Any of these on the resource shows the entry. Defaults to view. */
  permissions?: string[];
}

export type AppKey = 'scorecard' | 'admin' | 'inventory';

/** How each app is named to people, in the sidebar and on Administration. */
export const APP_LABELS: Record<AppKey, string> = {
  scorecard: 'Facility Scorecard',
  admin: 'Admin',
  inventory: 'Inventory',
};

/**
 * Whether someone's access reaches anything in an app, so a link to it only
 * appears when it would lead somewhere. These mirror each app's own menu.
 */
/** The resources each app checks, so the footer describes access in this app only. */
const APP_RESOURCES: Record<AppKey, string[]> = {
  scorecard: ['dailySummary', 'incidents', 'dashboard', 'scorecard', 'settings', 'period'],
  admin: ['dataImport', 'employees', 'schedules', 'reference', 'users', 'audit'],
  inventory: ['counts', 'setup'],
};

const REACHES: Record<AppKey, (can: PlatformState['can']) => boolean> = {
  scorecard: (can) =>
    ['dailySummary', 'incidents', 'dashboard', 'scorecard', 'settings'].some((r) => can(r, 'view'))
    || can('scorecard', 'viewSelf'),
  admin: (can) =>
    ['dataImport', 'employees', 'schedules', 'reference', 'users', 'audit'].some((r) => can(r, 'view')),
  inventory: (can) => can('counts', 'view') || can('setup', 'view'),
};

export function AppShell({
  app, badge, nav, version, helpLabel, locationName, banner, noLocationLabel = 'Own scorecard',
  allLocationsLabel = 'All locations',
}: {
  /** Which app this is, so the sidebar links to the others and not to itself. */
  app: AppKey;
  /** The app's name under the wordmark, e.g. "FACILITY SCORECARD". */
  badge: string;
  nav: NavEntry[];
  version: string;
  /** Tooltip and accessible name for the help link, e.g. "How to use the scorecard". */
  helpLabel: string;
  /** Turns a location code into the name the footer shows. */
  locationName: (code: string) => string;
  /** Rendered above every screen. */
  banner?: ReactNode;
  /** What the footer says for someone whose access reaches no location. */
  noLocationLabel?: string;
  /** What the footer says for access at every location — "All regions" where access is by region. */
  allLocationsLabel?: string;
}) {
  const { me, can, devUser, switchDevUser } = usePlatform();
  const visible = nav.filter((n) => (n.permissions ?? ['view']).some((p) => can(n.resource, p)));

  // The other apps this person can use. Addresses come from the API's
  // configuration, so the same build links correctly locally and in Azure.
  const addresses = getAuthConfig()?.apps ?? {};
  const otherApps = (Object.keys(APP_LABELS) as AppKey[])
    .filter((key) => key !== app && addresses[key] && REACHES[key](can))
    .map((key) => ({ key, label: APP_LABELS[key], url: addresses[key] }));

  // Roles and locations in THIS app. A scorecard manager who also holds an
  // Admin role everywhere should not read "All locations" in the scorecard.
  const reached = Object.entries(me.scopes)
    .filter(([key]) => !key.endsWith(':viewSelf') && APP_RESOURCES[app].includes(key.split(':')[0]))
    .map(([, codes]) => codes);
  const roleSummary = [...new Set(me.assignments.filter((a) => a.app === app).map((a) => a.roleName))].join(', ')
    || me.roleSummary;
  const scopeLabel =
    reached.length === 0 ? noLocationLabel
    : reached.some((codes) => codes === null) ? allLocationsLabel
    : [...new Set(reached.flatMap((codes) => codes ?? []))].map(locationName).join(', ');

  // Below 1024px the sidebar is an off-canvas drawer rather than a column, so
  // it needs an open state — and one that closes itself the moment the drawer
  // stops being useful: on navigation, on Escape, and on a click outside.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    // The page underneath must not scroll while the drawer covers it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <div className="app">
      {/* Mobile only — CSS hides this entirely at desktop widths, where the
          sidebar is already on screen and needs no opening. */}
      <header className="topbar">
        <button
          type="button"
          className="topbar__menu"
          aria-label="Open navigation menu"
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
          onClick={() => setMenuOpen(true)}
        >
          <span className="hamburger" aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>
        <div className="topbar__wordmark">CYNTOX</div>
      </header>

      {menuOpen && <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} />}

      <nav id="app-sidebar" className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
        <button
          type="button"
          className="sidebar__close"
          aria-label="Close navigation menu"
          onClick={() => setMenuOpen(false)}
        >
          ×
        </button>
        <div className="sidebar__brand">
          <div className="sidebar__wordmark">CYNTOX</div>
          <div className="sidebar__tagline">BIOHAZARD SOLUTIONS</div>
          <div className="sidebar__badge">{badge}</div>
        </div>

        {/* Development only. In production the role comes from Entra ID and
            there is no switcher at all. */}
        {me.devMode && (
          <>
            <div style={{ padding: '0 20px 6px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)' }}>
              VIEW AS <span style={{ color: 'var(--orange)' }}>· DEV ONLY</span>
            </div>
            <div style={{ padding: '0 20px 16px' }}>
              <select
                className="control"
                style={{ width: '100%' }}
                value={devUser ?? me.upn}
                onChange={(e) => switchDevUser(e.target.value)}
                aria-label="Simulate a role"
              >
                {(me.devUsers ?? []).map((u) => (
                  <option key={u.upn} value={u.upn}>{u.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="sidebar__nav">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item${isActive ? ' nav-item--active' : ''}`}
            >
              <span className="nav-item__dot" />
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="sidebar__footer">
          {otherApps.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, color: 'var(--muted)', marginBottom: 4 }}>
                OTHER APPS
              </div>
              {otherApps.map((a) => (
                <a
                  key={a.key}
                  href={a.url}
                  style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--blue)', textDecoration: 'none', padding: '2px 0' }}
                >
                  {a.label} →
                </a>
              ))}
            </div>
          )}
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, color: 'var(--muted)', marginBottom: 4 }}>
            SIGNED IN AS
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{me.displayName}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{roleSummary} · {scopeLabel}</div>
          {/* Only where there is a real session to end. In development the
              switcher above is how you change who you are, and signing out of
              a dev header would mean nothing. */}
          {!me.devMode && (
            <button
              className="btn btn--sm"
              style={{ width: '100%', marginTop: 10 }}
              onClick={() => { void signOut(); }}
            >
              Sign out
            </button>
          )}
          <div className="sidebar__version">
            <span>Version: {version}</span>
            <button
              type="button"
              className="sidebar__refresh"
              aria-label="Reload the page"
              title="Reload the page"
              onClick={() => { window.location.reload(); }}
            >
              <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
                <path
                  d="M13.2 8A5.2 5.2 0 1 1 11.6 4.2"
                  fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
                />
                <path
                  d="M13.4 1.9v3.4H10"
                  fill="none" stroke="currentColor" strokeWidth="1.7"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </button>
            {/* Opens the guide at the top. Sits with the version because both
                answer "what am I looking at" rather than anything about the
                data on screen. */}
            <Link
              to="/help"
              className="sidebar__refresh"
              aria-label={helpLabel}
              title={helpLabel}
            >
              <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
                <path
                  d="M5.9 6a2.1 2.1 0 1 1 2.9 1.9c-.5.3-.8.7-.8 1.3v.4"
                  fill="none" stroke="currentColor" strokeWidth="1.7"
                  strokeLinecap="round" strokeLinejoin="round"
                />
                <circle cx="8" cy="12.4" r="0.95" fill="currentColor" />
              </svg>
            </Link>
          </div>
        </div>
      </nav>

      <main className="main">
        {/* Rendered once, above every screen. Putting these here rather than in
            each page header is what makes "every screen" true and keeps it
            true as screens are added. */}
        {banner}
        <Outlet />
      </main>
    </div>
  );
}
